import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Box,
  Button,
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import {
  addProgressAnalysis,
  getExams,
  getAllExternalExams,
  getSubmisionData,
} from "../../../../services/Exam";
import { getLocalDate, sortByOldestDate } from "../../../../utils/dateHelper";
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "../../../../styles/muiStylesHelper";
import Alert from "../../../../components/UI/Alert";
import SnackbarMessage from "../../../../components/UI/SnackbarMessage";
import SelectAnalysisExamCard from "./SelectAnalysisExamCard";
import FetchExam from "./FetchExam";
import ProgressionChartLegend from "../../../../components/UI/ProgressionChartLegend";
import SelectAnalysisExternalExamCard from "./SelectAnalysisExternalExamCard";
import useClinic from "../../../../hooks/useClinic";
import { useComponentDimensionsById } from "../../../../hooks/useComponentDimensionsById";
import { formatProgressionAnalysisData } from "../../../../utils/examReportsHelper";
import ProgressionCharts from "../../../../components/UI/ProgressionCharts";
import {
  getFilteredExams,
  getValidExams,
  getValidExternalExams,
} from "../../../../utils/progressionAnalysisHelper";
import { getTruncatedText } from "../../../../utils/textHelper";

const DEFAULT_EYE_SELECTION = "Left";
const MAX_EXAM_SELECTION = 10;

const ProgressAnalysis = ({
  open,
  setOpen,
  clinicId,
  selectedPatients,
  patient,
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();

  // clinic context
  const { clinicLicensing, clinicTimezone } = useClinic();

  // navigate
  const navigate = useNavigate();

  const [selectedEye, setSelectedEye] = useState(DEFAULT_EYE_SELECTION);

  const [regularExams, setRegularExams] = useState([]);
  const [externalExams, setExternalExams] = useState([]);
  const [sortedExams, setSortedExams] = useState([]);

  const [selectedExams, setSelectedExams] = useState([]);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openAddExamSnackbar, setOpenAddExamSnackbar] = useState(false);
  const [addExamSuccess, setAddExamSuccess] = useState(false);
  const [addExamFailed, setAddExamFailed] = useState(false);

  const [progressionChartData, setProgressionChartData] = useState([]);

  // Forces the scrolling chart container to be the same height as the ProgressionChart container
  const { dynamicHeight } = useComponentDimensionsById(
    "72vh",
    "#progression-chart-container"
  );

  // Filter the "Left" or "Right" eye exams from external and regular exams
  const filterExamsByEye = useCallback(
    (selectedEye) => {
      const filteredExternalExams = externalExams.filter(
        (exam) => exam.eye === selectedEye
      );
      const filteredRegularExams = regularExams
        .map((exam) => ({
          ...exam,
          visualFieldThresholdingSections:
            exam?.visualFieldThresholdingSections?.filter(
              (section) => section.eye === selectedEye
            ),
        }))
        .filter((exam) => exam?.visualFieldThresholdingSections?.length > 0);

      const filteredExams = filteredRegularExams?.concat(filteredExternalExams);
      const sortedExams = sortByOldestDate(filteredExams, "completionDate");
      setSortedExams(sortedExams);
    },
    [externalExams, regularExams]
  );

  // Logic to run when changing the eye type ("Left" or "Right")
  const changeEye = useCallback(
    (eyeValue) => {
      setSelectedExams([]);
      setProgressionChartData([]);
      setSelectedEye(eyeValue);
      filterExamsByEye(eyeValue);
    },
    [filterExamsByEye]
  );

  // Reset data on the drawer back to the default eye (Left)
  const resetData = () => {
    changeEye(DEFAULT_EYE_SELECTION);
  };

  const updateCharts = useCallback(
    (selectedExams) => {
      const newChart = formatProgressionAnalysisData(
        selectedExams,
        clinicTimezone
      );
      setProgressionChartData([...newChart]);
    },
    [clinicTimezone]
  );

  const deselectExam = useCallback(
    (exam) => {
      const updatedSelection = selectedExams.filter((e) => e.id !== exam.id);
      setSelectedExams(updatedSelection);
      updateCharts(updatedSelection);
    },
    [selectedExams, updateCharts]
  );

  const handleExternalExamSelect = useCallback(
    (exam) => {
      // remove exam
      if (selectedExams.some((e) => e.id === exam.id)) {
        deselectExam(exam);
      }
      // select exam
      else {
        if (selectedExams.length > 9) {
          setOpenSnackbar(true);
          return;
        }
        const updatedSelection = [...selectedExams, exam];
        const sortedExams = sortByOldestDate(
          updatedSelection,
          "completionDate"
        );
        setSelectedExams(sortedExams);
        updateCharts(sortedExams);
      }
    },
    [selectedExams, deselectExam, updateCharts]
  );

  const handleExamSelect = useCallback(
    async (exam) => {
      // remove exam
      if (selectedExams.some((e) => e.id === exam.id)) {
        deselectExam(exam);
      }
      // select exam
      else {
        if (selectedExams.length > 9) {
          setOpenSnackbar(true);
          return;
        }

        try {
          const visualFieldSections = exam?.visualFieldThresholdingSections;

          const token = await getAccessTokenSilently();
          const visualFieldSectionId = visualFieldSections?.[0]?.id;
          const reportData = await getSubmisionData(
            token,
            clinicId,
            selectedPatients[0],
            exam.id,
            visualFieldSectionId
          );

          if (reportData) {
            const parsedReportData = JSON.parse(reportData);

            if (
              parsedReportData?.postProcessing?.retinaLogikFieldIndex !== null
            ) {
              parsedReportData.postProcessing.retinaLogikFieldIndex *= 100;
            }
            if (parsedReportData?.stats?.fixationLossPercentage != null) {
              parsedReportData.stats.fixationLossPercentage *= 100;
            }
            if (parsedReportData?.stats?.falsePositivePercentage != null) {
              parsedReportData.stats.falsePositivePercentage *= 100;
            }

            const enrichedExam = {
              ...exam,
              reportData: parsedReportData,
              visualFieldSectionId,
            };

            const updatedSelection = [...selectedExams, enrichedExam];
            const sortedExams = sortByOldestDate(
              updatedSelection,
              "completionDate"
            );
            setSelectedExams(sortedExams);
            updateCharts(sortedExams);
          }
        } catch (err) {
          console.error(`Error fetching report data for exam ${exam.id}:`, err);
        }
      }
    },
    [
      updateCharts,
      getAccessTokenSilently,
      deselectExam,
      clinicId,
      selectedExams,
      selectedPatients,
    ]
  );

  const handleAddProgressAnalysis = async () => {
    if (selectedExams.length <= 2) {
      setOpenAddExamSnackbar(true);
      return;
    }
    const examIds = selectedExams
      .filter((exam) => exam.isRetinaLogikExam)
      .map((exam) => exam.id);
    const externalExamIds = selectedExams
      .filter((exam) => !exam.isRetinaLogikExam)
      .map((exam) => exam.id);
    const examData = { eye: selectedEye, examIds, externalExamIds };
    try {
      const token = await getAccessTokenSilently();
      const response = await addProgressAnalysis(
        token,
        clinicId,
        selectedPatients[0],
        examData
      );
      setAddExamSuccess(true);
      setOpen(true);
      setTimeout(() => {
        setOpen(false);
        const reportUrl = `/patients/${selectedPatients[0]}/progression-analysis/${response.data.id}`;
        navigate(reportUrl);
      }, 1000);
    } catch (error) {
      setAddExamSuccess(false);
      setAddExamFailed(true);
      setOpen(true);
      setTimeout(() => {
        setOpen(false);
      }, 5000);
    }
  };

  useEffect(() => {
    // Fetch regular exams
    const fetchExams = async () => {
      if (!selectedPatients || selectedPatients.length !== 1) return [];

      try {
        const token = await getAccessTokenSilently();
        const examsData = await getExams(token, clinicId, selectedPatients[0]);
        return examsData ?? [];
      } catch (error) {
        console.error("Error fetching exams:", error);
      }
    };

    // Fetch external exams
    const fetchExternalExams = async () => {
      if (!clinicLicensing?.canUseExternalReports) return [];
      if (!selectedPatients || selectedPatients.length !== 1) return [];

      try {
        const token = await getAccessTokenSilently();
        const externalExamsData = await getAllExternalExams(
          token,
          clinicId,
          selectedPatients[0]
        );
        return externalExamsData ?? [];
      } catch (error) {
        console.error("Error fetching external exams:", error);
      }
    };

    fetchExams().then((exams) => {
      fetchExternalExams().then((externalExams) => {
        // only return exams with valid grid types and statuses
        const validExams = getValidExams(exams);
        const validExternalExams = getValidExternalExams(externalExams);

        setRegularExams(validExams);
        setExternalExams(validExternalExams);

        // filter exams by the currently selected eye
        const initialSelectedEye = "Left";
        const filteredExams = getFilteredExams(
          validExternalExams,
          validExams,
          initialSelectedEye
        );

        // sort exams by the completion date
        const sortedExams = sortByOldestDate(filteredExams, "completionDate");
        setSortedExams(sortedExams);
      });
    });
  }, [
    getAccessTokenSilently,
    clinicId,
    selectedPatients,
    clinicLicensing?.canUseExternalReports,
  ]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => {
          resetData();
          setOpen(false);
        }}
        PaperProps={{
          sx: {
            width: "100%",
            height: "100%",
          },
        }}
      >
        <Box
          sx={{
            height: "100vh",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              py: 1,
              px: 2,
              gap: 2,
              backgroundColor: "white",
              borderBottom: "1px solid black",
              position: "relative",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography fontWeight="bold">
                {t("canUseProgressionReports_check")} /{" "}
                {getTruncatedText(
                  `${patient?.firstName} ${patient?.lastName}`,
                  15
                )}{" "}
                / {getLocalDate(patient?.dateOfBirth, clinicTimezone)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                alignItems: "center",
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                sx={ModalCustomCancel}
                onClick={() => {
                  resetData();
                  setOpen(false);
                }}
              >
                {t("progress_analysis_cancel")}
              </Button>
              <Button
                variant="outlined"
                sx={ModalCustomConfirm}
                onClick={handleAddProgressAnalysis}
                disabled={selectedExams?.length < 3}
              >
                {t("progress_analysis_generate")}
              </Button>
            </Box>
          </Box>

          <Grid container>
            <Grid item xs={12} md={4}>
              <Box
                id="progression-chart-container"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                  width: "100%",
                  minHeight: "72vh",
                  height: "100%",
                  padding: 2,
                  borderRight: { xs: "none", md: "1px solid black" },
                  borderBottom: { xs: "1px solid black", md: "none" },
                }}
              >
                <ProgressionChartLegend />
                <ProgressionCharts
                  chartData={progressionChartData}
                  chartHeight={200}
                />
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={8}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                height: dynamicHeight,
                overflowY: "auto",
                padding: 2,
              }}
            >
              {selectedExams.filter((exam) => exam.isRetinaLogikExam).length ===
              0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <Typography>{t("exams_select_rule")}</Typography>
                  <Typography>{t("exams_select_rule_2")}</Typography>
                </Box>
              ) : (
                selectedExams
                  .filter((exam) => exam.isRetinaLogikExam)
                  .map((exam) => {
                    return (
                      <Box key={exam.id}>
                        <FetchExam selectedExam={exam} />
                      </Box>
                    );
                  })
              )}
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              borderTop: "1px solid black",
              py: 1,
              px: 2,
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: "bold" }}>
                {t("progress_analysis_select_eye")}
              </Typography>

              <RadioGroup
                row
                value={selectedEye}
                onChange={(e) => changeEye(e.target.value)}
                sx={{ marginLeft: 2 }}
              >
                <FormControlLabel
                  value="Left"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: "#E2772E",
                        },
                      }}
                    />
                  }
                  label={t("word_left")}
                />

                <FormControlLabel
                  value="Right"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: "#E2772E",
                        },
                      }}
                    />
                  }
                  label={t("word_right")}
                />
              </RadioGroup>
            </Box>
            {selectedExams.length >= MAX_EXAM_SELECTION && (
              <Alert
                variant="error"
                alertContent={t("exams_select_rule")}
                padding="8px"
              />
            )}
            {/* Added this box for spacing purposes */}
            {selectedExams.length >= MAX_EXAM_SELECTION && (
              <Box width="250px" />
            )}
          </Box>

          <Stack
            direction="row"
            gap={2}
            sx={{
              px: 2,
              paddingBottom: 2,
              overflowX: "auto",
              "&::-webkit-scrollbar": { height: "8px" },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
            }}
          >
            {sortedExams.map((exam) => {
              const isSelected = selectedExams.some((e) => e.id === exam.id);
              const selectedExam = selectedExams.find((e) => e.id === exam.id);
              const isDisabled =
                !isSelected && selectedExams.length >= MAX_EXAM_SELECTION;

              return exam.isRetinaLogikExam ? (
                <SelectAnalysisExamCard
                  key={exam.id}
                  exam={exam}
                  isSelected={isSelected}
                  selectedExam={selectedExam}
                  handleCardSelect={handleExamSelect}
                  isDisabled={isDisabled}
                />
              ) : (
                <SelectAnalysisExternalExamCard
                  key={exam.id}
                  exam={exam}
                  isSelected={isSelected}
                  selectedExam={selectedExam}
                  handleCardSelect={handleExternalExamSelect}
                  isDisabled={isDisabled}
                />
              );
            })}
          </Stack>
        </Box>

        <SnackbarMessage
          open={openSnackbar}
          onClose={() => setOpenSnackbar(false)}
          text={t("exams_select_rule")}
          position={{ vertical: "top", horizontal: "right" }}
        />

        <SnackbarMessage
          open={openAddExamSnackbar}
          onClose={() => setOpenAddExamSnackbar(false)}
          text={t("exams_add_rule")}
          position={{ vertical: "top", horizontal: "right" }}
        />

        <SnackbarMessage
          open={addExamSuccess}
          onClose={() => setAddExamSuccess(false)}
          success={true}
          text={t("exams_add_analysis_success")}
          position={{ vertical: "top", horizontal: "right" }}
        />

        <SnackbarMessage
          open={addExamFailed}
          onClose={() => setAddExamFailed(false)}
          text={t("exams_add_analysis_failed")}
          position={{ vertical: "top", horizontal: "right" }}
        />
      </Drawer>
    </>
  );
};

export default ProgressAnalysis;
